import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';
import { validateResourceUrl } from '../utils/validation.js';

export interface VolcesSeedream30Params {
  /**
   * 提示词，必填，长度必须在1-800个字符之间
   */
  prompt: string;
  /**
   * 是否使用预处理LLM，默认值为false
   * @default false
   */
  use_pre_llm?: boolean;
  /**
   * 随机种子，-1或正整数，默认值为-1
   * @default -1
   */
  seed?: number;
  /**
   * 引导强度，范围1-10，默认值为2.5
   * @default 2.5
   */
  scale?: number;
  /**
   * 图像宽度，范围512-2048，默认值为1328
   * @default 1328
   */
  width?: number;
  /**
   * 图像高度，范围512-2048，默认值为1328
   * @default 1328
   */
  height?: number;
}

export interface VolcesSeedream30Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seedream_3_0
 *
 * 通过火山引擎Seedream 3.0 API创建图像生成任务。[API Documentation](https://www.volcengine.com/docs/86081/1804549)
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeedream30 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeedream30({
 *   // 参数见 VolcesSeedream30Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeedream30Params 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeedream30(
  params: VolcesSeedream30Params,
): Promise<VolcesSeedream30Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seedream_3_0',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesSeedream30Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          // Validate URL before adding to results
          validateResourceUrl(resource, 'image');

          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
